import { useState, useEffect, useRef } from 'react';
import {
  MapPin,
  Search,
  Loader2,
  ExternalLink,
  Crosshair,
  ZoomIn,
  ZoomOut,
  Compass,
  X,
  Route as RouteIcon,
} from 'lucide-react';
import { toast } from 'react-hot-toast';
import { fetchRoute, type RouteResult } from '@/shared/utils/routing';
import { L, createEsriTileLayer } from '@/shared/lib/leaflet';

interface SearchResultItem {
  id: string;
  title: string;
  subtitle: string;
  lat: string;
  lng: string;
  source: string;
}

interface GpsLocationPickerMapProps {
  lat: string;
  lng: string;
  locationName?: string;
  onChange: (coords: { lat: string; lng: string; address?: string }) => void;
  /** Where the route preview starts from — the rep's depot/branch, if known.
   *  Falls back to the same default Pune/Bhosari hub every other delivery/
   *  visit default in this codebase already uses when nothing more specific
   *  is available. */
  origin?: { lat: number; lng: number; label?: string };
}

const DEFAULT_ORIGIN = { lat: 18.6279, lng: 73.8131, label: 'RBM Depot (Pune/Bhosari)' };

// Quick presets for major industrial & commercial hubs
const POPULAR_HUBS = [
  { name: 'Ahmedabad', lat: '23.022500', lng: '72.571400' },
  { name: 'Mumbai', lat: '19.076000', lng: '72.877700' },
  { name: 'Delhi NCR', lat: '28.613900', lng: '77.209000' },
  { name: 'Bengaluru', lat: '12.971600', lng: '77.594600' },
  { name: 'Pune', lat: '18.520400', lng: '73.856700' },
  { name: 'Surat', lat: '21.170200', lng: '72.831100' },
  { name: 'Vadodara', lat: '22.307200', lng: '73.181200' },
];

export function GpsLocationPickerMap({
  lat,
  lng,
  locationName,
  onChange,
  origin,
}: GpsLocationPickerMapProps) {
  const [searchQuery, setSearchQuery] = useState(locationName || '');
  const [searchResults, setSearchResults] = useState<SearchResultItem[]>([]);
  const [isSearching, setIsSearching] = useState(false);
  const [showDropdown, setShowDropdown] = useState(false);
  const [isLocating, setIsLocating] = useState(false);
  const dropdownRef = useRef<HTMLDivElement>(null);
  const searchTimeoutRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  const mapContainerRef = useRef<HTMLDivElement>(null);
  const mapInstanceRef = useRef<L.Map | null>(null);
  const markerRef = useRef<L.Marker | null>(null);
  const polylineRef = useRef<L.Polyline | null>(null);
  const originMarkerRef = useRef<L.CircleMarker | null>(null);

  const numLat = parseFloat(lat) || 23.0225;
  const numLng = parseFloat(lng) || 72.5714;
  const hasValidCoords = Boolean(lat && lng && !isNaN(parseFloat(lat)) && !isNaN(parseFloat(lng)));

  const routeOrigin = origin || DEFAULT_ORIGIN;
  const [routeInfo, setRouteInfo] = useState<RouteResult | null>(null);
  const [routing, setRouting] = useState(false);

  // Initialize interactive Leaflet map instance (100% Free of Cost, Zero API Key, No 403 Forbidden)
  useEffect(() => {
    if (!mapContainerRef.current) return;
    if (mapInstanceRef.current) return;

    try {
      const map = L.map(mapContainerRef.current, {
        zoomControl: false,
        attributionControl: false,
      }).setView([numLat, numLng], 14);

      // Add high-resolution, rock-solid ESRI World Street Basemap
      createEsriTileLayer().addTo(map);

      // Click on map to pick location
      map.on('click', (e: L.LeafletMouseEvent) => {
        const cLat = e.latlng.lat.toFixed(6);
        const cLng = e.latlng.lng.toFixed(6);
        onChange({ lat: cLat, lng: cLng });

        // Reverse geocode to find address name
        fetch(`https://photon.komoot.io/reverse?lat=${e.latlng.lat}&lon=${e.latlng.lng}`)
          .then((r) => r.json())
          .then((data) => {
            const feat = data?.features?.[0];
            if (feat?.properties) {
              const p = feat.properties;
              const name = [p.name, p.street, p.city, p.district].filter(Boolean).join(', ');
              if (name) {
                setSearchQuery(name);
                onChange({ lat: cLat, lng: cLng, address: name });
              }
            }
          })
          .catch(() => {});
      });

      mapInstanceRef.current = map;
    } catch (err) {
      console.warn('Map initialization error:', err);
    }

    return () => {
      if (mapInstanceRef.current) {
        mapInstanceRef.current.remove();
        mapInstanceRef.current = null;
      }
    };
  }, []);

  // Update marker and map position when coords change
  useEffect(() => {
    const map = mapInstanceRef.current;
    if (!map) return;

    map.setView([numLat, numLng], Math.max(map.getZoom(), 14), { animate: true });

    if (hasValidCoords) {
      const pinIcon = L.divIcon({
        className: 'custom-gps-picker-pin',
        html: `
          <div style="position: relative; display: flex; flex-direction: column; align-items: center; cursor: grab;">
            <div style="width: 34px; height: 34px; border-radius: 50% 50% 50% 0; transform: rotate(-45deg); background: #e11d48; border: 3px solid #ffffff; box-shadow: 0 4px 12px rgba(0,0,0,0.35); display: flex; align-items: center; justify-content: center;">
              <span style="transform: rotate(45deg); color: #ffffff; font-size: 15px;">📍</span>
            </div>
            <div style="width: 8px; height: 8px; border-radius: 50%; background: rgba(0,0,0,0.25); margin-top: 1px;"></div>
          </div>
        `,
        iconSize: [34, 44],
        iconAnchor: [17, 42],
      });

      if (!markerRef.current) {
        const marker = L.marker([numLat, numLng], {
          icon: pinIcon,
          draggable: true,
        }).addTo(map);

        marker.on('dragend', () => {
          const pos = marker.getLatLng();
          const dLat = pos.lat.toFixed(6);
          const dLng = pos.lng.toFixed(6);
          onChange({ lat: dLat, lng: dLng });

          fetch(`https://photon.komoot.io/reverse?lat=${pos.lat}&lon=${pos.lng}`)
            .then((r) => r.json())
            .then((data) => {
              const feat = data?.features?.[0];
              if (feat?.properties) {
                const p = feat.properties;
                const name = [p.name, p.street, p.city, p.district].filter(Boolean).join(', ');
                if (name) {
                  setSearchQuery(name);
                  onChange({ lat: dLat, lng: dLng, address: name });
                }
              }
            })
            .catch(() => {});
        });

        markerRef.current = marker;
      } else {
        markerRef.current.setLatLng([numLat, numLng]);
      }
    } else if (markerRef.current) {
      markerRef.current.remove();
      markerRef.current = null;
    }
  }, [numLat, numLng, hasValidCoords]);

  // Update Route Polyline between origin and destination
  useEffect(() => {
    const map = mapInstanceRef.current;
    if (!map) return;

    if (polylineRef.current) {
      polylineRef.current.remove();
      polylineRef.current = null;
    }
    if (originMarkerRef.current) {
      originMarkerRef.current.remove();
      originMarkerRef.current = null;
    }

    if (hasValidCoords && routeOrigin?.lat && routeOrigin?.lng) {
      // Depot origin marker
      const oMarker = L.circleMarker([routeOrigin.lat, routeOrigin.lng], {
        radius: 7,
        color: '#4f46e5',
        fillColor: '#4f46e5',
        fillOpacity: 1,
        weight: 2,
      }).addTo(map);
      oMarker.bindTooltip(routeOrigin.label || 'Depot', { direction: 'top', offset: [0, -6] });
      originMarkerRef.current = oMarker;

      // Connecting route polyline
      const poly = L.polyline(
        [
          [routeOrigin.lat, routeOrigin.lng],
          [numLat, numLng],
        ],
        {
          color: '#4f46e5',
          weight: 4,
          opacity: 0.85,
          dashArray: '8, 8',
        }
      ).addTo(map);

      polylineRef.current = poly;
    }
  }, [hasValidCoords, numLat, numLng, routeOrigin]);

  // Fetch route distance and ETA preview
  useEffect(() => {
    if (!hasValidCoords) {
      setRouteInfo(null);
      return;
    }
    let cancelled = false;
    setRouting(true);
    fetchRoute({ lat: routeOrigin.lat, lng: routeOrigin.lng }, { lat: numLat, lng: numLng }).then((route) => {
      if (!cancelled) {
        setRouteInfo(route);
        setRouting(false);
      }
    });
    return () => {
      cancelled = true;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [numLat, numLng, hasValidCoords]);

  // Close dropdown on outside click
  useEffect(() => {
    function handleClickOutside(event: MouseEvent) {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target as Node)) {
        setShowDropdown(false);
      }
    }
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  // Check if string contains coordinates like "23.0225, 72.5714"
  const parseDirectCoordinates = (query: string): SearchResultItem | null => {
    const clean = query.replace(/[^\d.,\s-]/g, '').trim();
    const parts = clean.split(/[,\s]+/).filter(Boolean);
    if (parts.length === 2) {
      const pLat = parseFloat(parts[0]);
      const pLng = parseFloat(parts[1]);
      if (!isNaN(pLat) && !isNaN(pLng) && pLat >= -90 && pLat <= 90 && pLng >= -180 && pLng <= 180) {
        return {
          id: 'direct-coords',
          title: `Go to Coordinates: ${pLat.toFixed(6)}, ${pLng.toFixed(6)}`,
          subtitle: 'Direct GPS Latitude / Longitude input',
          lat: pLat.toFixed(6),
          lng: pLng.toFixed(6),
          source: 'GPS Coordinate Match',
        };
      }
    }
    return null;
  };

  // Robust Multi-Engine Search (Photon Komoot + Nominatim Fallback)
  const performSearch = async (query: string) => {
    const results: SearchResultItem[] = [];
    const directCoord = parseDirectCoordinates(query);
    if (directCoord) {
      results.push(directCoord);
    }

    try {
      // 1. Primary: Photon Komoot API (High speed, typo-tolerant, global OSM dataset)
      const photonRes = await fetch(
        `https://photon.komoot.io/api/?q=${encodeURIComponent(query.trim())}&limit=8`
      );
      if (photonRes.ok) {
        const data = await photonRes.json();
        if (Array.isArray(data?.features)) {
          for (const feat of data.features) {
            const coords = feat.geometry?.coordinates;
            const props = feat.properties || {};
            if (coords && coords.length >= 2) {
              const name = props.name || props.street || props.city || props.district || query;
              const subparts = [
                props.street,
                props.district,
                props.city,
                props.state,
                props.postcode,
                props.country,
              ].filter(Boolean);

              results.push({
                id: `photon-${props.osm_id || Math.random()}`,
                title: name,
                subtitle: subparts.join(', ') || name,
                lat: parseFloat(coords[1]).toFixed(6),
                lng: parseFloat(coords[0]).toFixed(6),
                source: props.type || 'Photon OSM',
              });
            }
          }
        }
      }
    } catch (err) {
      console.warn('Photon geocoding error:', err);
    }

    // 2. Fallback: If Photon had fewer than 3 results, query Nominatim OpenStreetMap
    if (results.length < 3) {
      try {
        const nomRes = await fetch(
          `https://nominatim.openstreetmap.org/search?format=json&q=${encodeURIComponent(
            query.trim()
          )}&limit=6&addressdetails=1`
        );
        if (nomRes.ok) {
          const nomData = await nomRes.json();
          if (Array.isArray(nomData)) {
            for (const item of nomData) {
              const mainTitle = item.display_name.split(',')[0];
              // Avoid duplicates
              if (!results.some((r) => r.title.toLowerCase() === mainTitle.toLowerCase())) {
                results.push({
                  id: `nom-${item.place_id}`,
                  title: mainTitle,
                  subtitle: item.display_name,
                  lat: parseFloat(item.lat).toFixed(6),
                  lng: parseFloat(item.lon).toFixed(6),
                  source: 'OpenStreetMap',
                });
              }
            }
          }
        }
      } catch (err) {
        console.warn('Nominatim geocoding error:', err);
      }
    }

    return results;
  };

  const handleSearchChange = (val: string) => {
    setSearchQuery(val);
    if (searchTimeoutRef.current) clearTimeout(searchTimeoutRef.current);

    if (val.trim().length < 2) {
      setSearchResults([]);
      setShowDropdown(false);
      return;
    }

    setIsSearching(true);
    searchTimeoutRef.current = setTimeout(async () => {
      const items = await performSearch(val);
      setSearchResults(items);
      setIsSearching(false);
      setShowDropdown(true);
    }, 350);
  };

  const handleSelectPlace = (item: SearchResultItem) => {
    onChange({
      lat: item.lat,
      lng: item.lng,
      address: item.subtitle || item.title,
    });

    setSearchQuery(item.title);
    setShowDropdown(false);
    toast.success(`Selected: ${item.title}`);
  };

  const handleCaptureDeviceGps = () => {
    if (!navigator.geolocation) {
      toast.error('Geolocation is not supported by your browser.');
      return;
    }

    setIsLocating(true);
    navigator.geolocation.getCurrentPosition(
      async (pos) => {
        const curLat = pos.coords.latitude.toFixed(6);
        const curLng = pos.coords.longitude.toFixed(6);

        // Reverse geocode with Photon / Nominatim
        let placeName = 'My Current Location';
        try {
          const res = await fetch(
            `https://photon.komoot.io/reverse?lat=${curLat}&lon=${curLng}`
          );
          if (res.ok) {
            const data = await res.json();
            const feat = data?.features?.[0];
            if (feat?.properties) {
              const props = feat.properties;
              placeName = [props.name, props.street, props.city, props.state]
                .filter(Boolean)
                .join(', ');
            }
          }
        } catch {
          // fallback
        }

        onChange({
          lat: curLat,
          lng: curLng,
          address: placeName,
        });
        setSearchQuery(placeName);
        setIsLocating(false);
        toast.success(`Location captured: ${curLat}, ${curLng}`);
      },
      (err) => {
        setIsLocating(false);
        toast.error(err.message || 'Unable to retrieve your location.');
      },
      { enableHighAccuracy: true, timeout: 10000, maximumAge: 0 }
    );
  };

  const handleApplyPreset = (hub: (typeof POPULAR_HUBS)[number]) => {
    onChange({
      lat: hub.lat,
      lng: hub.lng,
      address: `${hub.name}, India`,
    });
    setSearchQuery(`${hub.name}, India`);
    toast.success(`Centered on ${hub.name}`);
  };

  return (
    <div className="space-y-3.5 rounded-2xl border border-slate-200 bg-slate-50/60 p-4 shadow-xs">
      {/* ── HEADER ── */}
      <div className="flex flex-wrap items-center justify-between gap-2">
        <div className="flex items-center gap-2">
          <div className="flex h-7 w-7 items-center justify-center rounded-lg bg-indigo-600 text-white shadow-xs">
            <Compass className="h-4 w-4" />
          </div>
          <div>
            <h4 className="text-xs font-bold text-slate-800">Smart GPS Map & Universal Place Search</h4>
            <p className="text-[11px] text-slate-500">
              Interactive map · Click anywhere or drag the red pin to set location
            </p>
          </div>
        </div>

        <button
          type="button"
          onClick={handleCaptureDeviceGps}
          disabled={isLocating}
          className="inline-flex items-center gap-1.5 rounded-xl border border-blue-200 bg-blue-50 px-3 py-1.5 text-xs font-bold text-blue-700 hover:bg-blue-100 transition-colors disabled:opacity-50"
        >
          {isLocating ? (
            <Loader2 className="h-3.5 w-3.5 animate-spin text-blue-600" />
          ) : (
            <Crosshair className="h-3.5 w-3.5 text-blue-600" />
          )}
          {isLocating ? 'Acquiring GPS…' : 'Use Current Device GPS'}
        </button>
      </div>

      {/* ── SEARCH INPUT WITH AUTOCOMPLETE DROPDOWN ── */}
      <div className="relative" ref={dropdownRef}>
        <div className="relative flex items-center">
          <Search className="absolute left-3 h-4 w-4 text-slate-400 pointer-events-none" />
          <input
            type="text"
            value={searchQuery}
            onChange={(e) => handleSearchChange(e.target.value)}
            onFocus={() => {
              if (searchResults.length > 0) setShowDropdown(true);
            }}
            placeholder="Type place name, factory, city, PIN code, or '23.0225, 72.5714'…"
            className="h-10 w-full rounded-xl border border-slate-200 bg-white pl-9 pr-9 text-xs font-medium text-slate-800 placeholder:text-slate-400 focus:border-blue-500 focus:outline-none focus:ring-2 focus:ring-blue-500/20 shadow-2xs"
          />
          {searchQuery && (
            <button
              type="button"
              onClick={() => {
                setSearchQuery('');
                setSearchResults([]);
                setShowDropdown(false);
              }}
              className="absolute right-3 text-slate-400 hover:text-slate-600 p-0.5"
            >
              <X className="h-3.5 w-3.5" />
            </button>
          )}
          {isSearching && (
            <div className="absolute right-3">
              <Loader2 className="h-4 w-4 animate-spin text-blue-600" />
            </div>
          )}
        </div>

        {/* Dropdown Results */}
        {showDropdown && searchResults.length > 0 && (
          <div className="absolute top-full left-0 right-0 z-50 mt-1 max-h-64 overflow-y-auto rounded-xl border border-slate-200 bg-white p-1.5 shadow-xl">
            <div className="px-2 py-1 text-[10px] font-bold uppercase tracking-wider text-slate-400">
              Select Location ({searchResults.length} matches)
            </div>
            {searchResults.map((item) => (
              <button
                key={item.id}
                type="button"
                onClick={() => handleSelectPlace(item)}
                className="flex w-full items-start gap-2.5 rounded-lg px-2.5 py-2 text-left hover:bg-slate-50 transition-colors"
              >
                <MapPin className="h-4 w-4 text-rose-500 shrink-0 mt-0.5" />
                <div className="min-w-0 flex-1">
                  <div className="text-xs font-bold text-slate-800 truncate">{item.title}</div>
                  <div className="text-[11px] text-slate-500 truncate">{item.subtitle}</div>
                  <div className="flex items-center gap-2 mt-0.5 text-[10px] text-slate-400 font-mono">
                    <span>
                      {item.lat}, {item.lng}
                    </span>
                    <span className="rounded bg-slate-100 px-1 py-0.5 text-[9px] font-sans">
                      {item.source}
                    </span>
                  </div>
                </div>
              </button>
            ))}
          </div>
        )}
      </div>

      {/* ── POPULAR COMMERCIAL HUBS QUICK SELECT ── */}
      <div className="flex flex-wrap items-center gap-1.5">
        <span className="text-[10px] font-bold uppercase tracking-wider text-slate-400 mr-1">
          Quick Hubs:
        </span>
        {POPULAR_HUBS.map((hub) => (
          <button
            key={hub.name}
            type="button"
            onClick={() => handleApplyPreset(hub)}
            className="rounded-lg border border-slate-200 bg-white px-2 py-1 text-[11px] font-semibold text-slate-600 hover:border-blue-400 hover:bg-blue-50/50 hover:text-blue-700 transition-colors"
          >
            {hub.name}
          </button>
        ))}
      </div>

      {/* ── LIVE INTERACTIVE MAP DISPLAY (100% FREE, NO 403, NO API KEY) ── */}
      <div className="relative overflow-hidden rounded-xl border border-slate-200 bg-slate-100 shadow-inner h-60">
        <div ref={mapContainerRef} className="h-full w-full z-0" />

        {/* Zoom & Navigation Controls */}
        <div className="absolute top-2 right-2 flex flex-col gap-1 z-10">
          <button
            type="button"
            onClick={() => mapInstanceRef.current?.zoomIn()}
            className="flex h-7 w-7 items-center justify-center rounded-lg bg-white/95 text-slate-700 shadow-md hover:bg-white transition-all text-xs font-bold border border-slate-200"
            title="Zoom In"
          >
            <ZoomIn className="h-3.5 w-3.5" />
          </button>
          <button
            type="button"
            onClick={() => mapInstanceRef.current?.zoomOut()}
            className="flex h-7 w-7 items-center justify-center rounded-lg bg-white/95 text-slate-700 shadow-md hover:bg-white transition-all text-xs font-bold border border-slate-200"
            title="Zoom Out"
          >
            <ZoomOut className="h-3.5 w-3.5" />
          </button>
          <button
            type="button"
            onClick={() => {
              if (mapInstanceRef.current && hasValidCoords) {
                mapInstanceRef.current.setView([numLat, numLng], 15, { animate: true });
              }
            }}
            className="flex h-7 w-7 items-center justify-center rounded-lg bg-white/95 text-slate-700 shadow-md hover:bg-white transition-all text-xs font-bold border border-slate-200"
            title="Center on Pin"
          >
            <Compass className="h-3.5 w-3.5 text-indigo-600" />
          </button>
        </div>

        {/* Pin Location Status Bar */}
        <div className="absolute bottom-2 left-2 right-2 flex items-center justify-between gap-2 rounded-lg bg-white/95 px-3 py-1.5 shadow-md backdrop-blur-xs text-[11px] z-10 border border-slate-200">
          <div className="flex items-center gap-1.5 text-slate-700 truncate">
            <MapPin className="h-3.5 w-3.5 text-rose-600 shrink-0" />
            <span className="font-bold truncate text-slate-800">
              {hasValidCoords ? `${lat}° N, ${lng}° E` : 'Click anywhere on map to drop pin'}
            </span>
          </div>
          {hasValidCoords && (
            <a
              href={`https://www.google.com/maps?q=${lat},${lng}`}
              target="_blank"
              rel="noopener noreferrer"
              className="inline-flex items-center gap-1 text-[10px] font-bold text-blue-600 hover:text-blue-700 hover:underline shrink-0"
            >
              Open in Google Maps
              <ExternalLink className="h-2.5 w-2.5" />
            </a>
          )}
        </div>
      </div>

      {/* ── ROUTE PREVIEW: DEPOT/REP -> DESTINATION ── */}
      {hasValidCoords && (
        <div className="flex items-center justify-between gap-2 rounded-xl border border-indigo-100 bg-indigo-50/70 px-3 py-2">
          <div className="flex items-center gap-2 text-[11px] text-indigo-900 min-w-0">
            <RouteIcon className="h-4 w-4 text-indigo-600 shrink-0" />
            <span className="truncate">
              From <b>{routeOrigin.label || 'depot'}</b> to this stop
            </span>
          </div>
          <span className="text-[11px] font-bold text-indigo-700 shrink-0">
            {routing ? 'Routing…' : routeInfo ? `${routeInfo.distanceKm} km · ~${routeInfo.durationMin} min` : '—'}
          </span>
        </div>
      )}

      {/* ── TWO-WAY LATITUDE & LONGITUDE INPUTS ── */}
      <div className="grid grid-cols-2 gap-3">
        <div>
          <label className="block text-[11px] font-bold text-slate-700 mb-1">
            Latitude <span className="text-rose-500">*</span>
          </label>
          <input
            type="number"
            step="any"
            value={lat}
            onChange={(e) => onChange({ lat: e.target.value, lng })}
            placeholder="e.g. 23.0225"
            className="h-9 w-full rounded-xl border border-slate-200 bg-white px-3 text-xs font-mono font-bold text-slate-900 placeholder:text-slate-400 focus:border-blue-500 focus:outline-none focus:ring-2 focus:ring-blue-500/20"
          />
        </div>

        <div>
          <label className="block text-[11px] font-bold text-slate-700 mb-1">
            Longitude <span className="text-rose-500">*</span>
          </label>
          <input
            type="number"
            step="any"
            value={lng}
            onChange={(e) => onChange({ lat, lng: e.target.value })}
            placeholder="e.g. 72.5714"
            className="h-9 w-full rounded-xl border border-slate-200 bg-white px-3 text-xs font-mono font-bold text-slate-900 placeholder:text-slate-400 focus:border-blue-500 focus:outline-none focus:ring-2 focus:ring-blue-500/20"
          />
        </div>
      </div>
    </div>
  );
}
